/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import {
  SafeAreaView,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  StatusBar,
  Modal,
  TextInput,
  Alert,
  ActivityIndicator,
  Image,
} from 'react-native';
import {
  Ionicons,
  MaterialCommunityIcons,
  FontAwesome,
  AntDesign,
} from '@expo/vector-icons';
import { useNavigation, useRoute } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import axios from 'axios';

// ==================== ✅ API CONFIGURATION ====================
const API_BASE_URL = 'https://api.jobeng.ai';
const API_ENDPOINTS = {
  projectById: (id) => `${API_BASE_URL}/api/projects/${id}`,
  projectByIdFallback: (id) => `${API_BASE_URL}/api/data/projects/${id}`,
  companyProfile: `${API_BASE_URL}/api/company/profile`,
};

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef',
  },
});

api.interceptors.request.use(
  async (config) => {
    try {
      const token = await AsyncStorage.getItem('auth_token') || await AsyncStorage.getItem('token');
      if (token) config.headers.Authorization = `Bearer ${token}`;
      const companyId = await AsyncStorage.getItem('company_id') || await AsyncStorage.getItem('company_id');
      if (companyId) config.params = { ...config.params, company_id: parseInt(companyId) };
    } catch (error) {
      console.error('Error in request interceptor:', error);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ✅ Normalise project from API — handles both team_members and team field names
const normaliseProject = (raw) => {
  if (!raw) return raw;
  const team = raw.team || raw.team_members || [];
  return {
    ...raw,
    // Normalise each member so name/role are always strings
    team: team.map((m, idx) => ({
      ...m,
      id: m.id || m.member_id || `t_${idx}_${Date.now()}`,
      name: m.name || m.member_name || '',
      role: m.role || m.member_role || '',
      avatar: m.avatar || m.avatar_url || 'https://randomuser.me/api/portraits/men/1.jpg',
    })),
  };
};

export default function ProjectTeamScreen() {
  const navigation = useNavigation();
  const route = useRoute();
  const { project } = route.params;

  const [loading, setLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [showEditMemberModal, setShowEditMemberModal] = useState(false);
  const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [teamMember, setTeamMember] = useState({
    name: '',
    role: '',
    avatar: 'https://randomuser.me/api/portraits/men/1.jpg',
  });
  const [projectData, setProjectData] = useState(null);
  const [filterRole, setFilterRole] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [companyLogo, setCompanyLogo] = useState(null);

  const avatarOptions = [
    'https://randomuser.me/api/portraits/men/1.jpg',
    'https://randomuser.me/api/portraits/women/1.jpg',
    'https://randomuser.me/api/portraits/men/2.jpg',
    'https://randomuser.me/api/portraits/women/2.jpg',
    'https://randomuser.me/api/portraits/men/3.jpg',
    'https://randomuser.me/api/portraits/women/3.jpg',
    'https://randomuser.me/api/portraits/men/4.jpg',
    'https://randomuser.me/api/portraits/women/4.jpg',
  ];

  useEffect(() => {
    loadCompanyLogo();
  }, []);

  const loadCompanyLogo = async () => {
    try {
      const cachedProfile = await AsyncStorage.getItem('company_profile');
      if (cachedProfile) {
        const profile = JSON.parse(cachedProfile);
        setCompanyLogo(profile.logo || null);
      }
      const companyId = await AsyncStorage.getItem('company_id') || await AsyncStorage.getItem('company_id');
      if (companyId) {
        const response = await api.get(`${API_BASE_URL}/api/company/profile/${companyId}`);
        if (response.data?.success && response.data.data) {
          const p = response.data.data;
          setCompanyLogo(p.logo || null);
          await AsyncStorage.setItem('company_profile', JSON.stringify(p));
        }
      }
    } catch (error) {
      console.warn('Error loading company logo:', error);
    }
  };

  // ✅ Normalise on load so team array is always present
  useEffect(() => {
    if (project) {
      setProjectData(normaliseProject(project));
    }
  }, [project]);

  const handleGoBack = () => navigation.goBack();

  const getUniqueRoles = () => {
    if (!projectData?.team?.length) return [];
    const roles = projectData.team.map((m) => m.role).filter(Boolean);
    return ['All', ...new Set(roles)];
  };

  // ==================== API SYNC ====================
  const syncProjectToAPI = async (updatedProject) => {
    if (!updatedProject?.id) {
      console.error('Cannot sync: missing project ID');
      return false;
    }
    setIsSaving(true);
    let success = false;
    const endpoints = [
      API_ENDPOINTS.projectById(updatedProject.id),
      API_ENDPOINTS.projectByIdFallback(updatedProject.id),
    ];
    for (const endpoint of endpoints) {
      try {
        const payload = {
          ...updatedProject,
          team_members: updatedProject.team.map((m) => ({
            id: m.id,
            name: m.name,
            role: m.role,
            avatar: m.avatar,
          })),
        };
        delete payload.team;
        delete payload.milestones;
        delete payload.risks;
        delete payload.tags;
        delete payload.recentUpdates;
        const response = await api.put(endpoint, payload);
        if (response.status === 200 || response.status === 204 || response.data?.success) {
          success = true;
          break;
        }
      } catch (error) {
        console.log(`❌ Update failed at ${endpoint}:`, error.message);
      }
    }
    if (!success) Alert.alert('Error', 'Failed to sync changes to server. Please try again.');
    setIsSaving(false);
    return success;
  };

  // ==================== HANDLERS ====================
  const handleAddTeamMember = async () => {
    if (!teamMember.name.trim() || !teamMember.role.trim()) {
      Alert.alert('Error', 'Please enter both name and role for the team member');
      return;
    }
    const newMember = { ...teamMember, id: `t${Date.now()}` };
    const updatedProject = { ...projectData, team: [...(projectData.team || []), newMember] };
    setProjectData(updatedProject);
    setTeamMember({ name: '', role: '', avatar: 'https://randomuser.me/api/portraits/men/1.jpg' });
    setShowAddMemberModal(false);
    const synced = await syncProjectToAPI(updatedProject);
    if (!synced) {
      setProjectData(projectData);
      setShowAddMemberModal(true);
    } else {
      Alert.alert('Success', 'Team member added successfully');
    }
  };

  const handleEditTeamMember = async () => {
    if (!teamMember.name.trim() || !teamMember.role.trim()) {
      Alert.alert('Error', 'Please enter both name and role for the team member');
      return;
    }
    const updatedTeam = (projectData.team || []).map((m) =>
      m.id === selectedMember.id ? { ...teamMember, id: m.id } : m
    );
    const updatedProject = { ...projectData, team: updatedTeam };
    setProjectData(updatedProject);
    setTeamMember({ name: '', role: '', avatar: 'https://randomuser.me/api/portraits/men/1.jpg' });
    setSelectedMember(null);
    setShowEditMemberModal(false);
    const synced = await syncProjectToAPI(updatedProject);
    if (!synced) {
      setProjectData(projectData);
      setShowEditMemberModal(true);
    } else {
      Alert.alert('Success', 'Team member updated successfully');
    }
  };

  const handleDeleteTeamMember = async () => {
    const updatedTeam = (projectData.team || []).filter((m) => m.id !== selectedMember.id);
    const updatedProject = { ...projectData, team: updatedTeam };
    setProjectData(updatedProject);
    setSelectedMember(null);
    setShowConfirmDeleteModal(false);
    const synced = await syncProjectToAPI(updatedProject);
    if (!synced) {
      setProjectData(projectData);
      setShowConfirmDeleteModal(true);
    } else {
      Alert.alert('Success', 'Team member removed successfully');
    }
  };

  const openEditMemberModal = (member) => {
    setSelectedMember(member);
    setTeamMember({ name: member.name, role: member.role, avatar: member.avatar });
    setShowEditMemberModal(true);
  };

  const openDeleteMemberConfirmation = (member) => {
    setSelectedMember(member);
    setShowConfirmDeleteModal(true);
  };

  const getFilteredTeamMembers = () => {
    if (!projectData?.team?.length) return [];
    let filtered = projectData.team;
    if (filterRole !== 'All') filtered = filtered.filter((m) => m.role === filterRole);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (m) =>
          (m.name || '').toLowerCase().includes(q) ||
          (m.role || '').toLowerCase().includes(q)
      );
    }
    return filtered;
  };

  // ✅ Safe sort — guards against undefined name
  const getSortedTeamMembers = () => {
    return getFilteredTeamMembers().sort((a, b) =>
      (a.name || '').localeCompare(b.name || '')
    );
  };

  if (!projectData) {
    return (
      <SafeAreaView style={styles.safeArea}>
        <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />
        <View style={styles.loadingContainer}>
          <ActivityIndicator size={36} color="#0e4e96" />
          <Text style={styles.loadingText}>Loading team members...</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />

      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity style={styles.backButton} onPress={handleGoBack}>
          <Ionicons name="chevron-back" size={24} color="#fff" />
        </TouchableOpacity>
        <View style={styles.headerCenter}>
          {companyLogo ? (
            <Image source={{ uri: companyLogo || undefined }} style={styles.headerLogo} />
          ) : (
            <MaterialCommunityIcons name="office-building" size={30} color="#fff" />
          )}
          <Text style={styles.headerTitle}>Project Team</Text>
        </View>
        <View style={{ width: 30 }} />
      </View>

      {/* Project Title */}
      <View style={styles.projectHeader}>
        <Text style={styles.projectTitle}>{projectData.title}</Text>
      </View>

      {/* Team Summary */}
      <View style={styles.teamSummary}>
        <View style={styles.teamStat}>
          <Text style={styles.teamStatValue}>{(projectData.team || []).length}</Text>
          <Text style={styles.teamStatLabel}>Team Members</Text>
        </View>
        <View style={styles.teamStat}>
          <Text style={styles.teamStatValue}>{Math.max(0, getUniqueRoles().length - 1)}</Text>
          <Text style={styles.teamStatLabel}>Unique Roles</Text>
        </View>
      </View>

      {/* Search and Filter */}
      <View style={styles.searchFilterContainer}>
        <View style={styles.searchContainer}>
          <Ionicons name="search" size={20} color="#64748B" />
          <TextInput
            style={styles.searchInput}
            placeholder="Search team members..."
            placeholderTextColor="#94A3B8"
            value={searchQuery}
            onChangeText={setSearchQuery}
          />
          {searchQuery.length > 0 && (
            <TouchableOpacity onPress={() => setSearchQuery('')}>
              <Ionicons name="close-circle" size={20} color="#64748B" />
            </TouchableOpacity>
          )}
        </View>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.filterContainer}
        >
          {getUniqueRoles().map((role) => (
            <TouchableOpacity
              key={role}
              style={[styles.filterOption, filterRole === role && styles.filterOptionActive]}
              onPress={() => setFilterRole(role)}
            >
              <Text style={[styles.filterOptionText, filterRole === role && styles.filterOptionTextActive]}>
                {role}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>

      {/* Add Button */}
      <View style={styles.addButtonContainer}>
        <TouchableOpacity
          style={styles.addButton}
          onPress={() => setShowAddMemberModal(true)}
          disabled={isSaving}
        >
          <Ionicons name="add" size={20} color="#fff" />
          <Text style={styles.addButtonText}>Add Team Member</Text>
        </TouchableOpacity>
      </View>

      {/* Saving Overlay */}
      {isSaving && (
        <View style={styles.savingOverlay}>
          <ActivityIndicator size={36} color="#0e4e96" />
          <Text style={styles.savingText}>Saving changes...</Text>
        </View>
      )}

      {/* Team Members List */}
      <ScrollView style={styles.scrollContainer} contentContainerStyle={styles.contentContainer}>
        {getSortedTeamMembers().length === 0 ? (
          <View style={styles.emptyState}>
            <MaterialCommunityIcons name="account-group" size={40} color="#CBD5E1" />
            <Text style={styles.emptyStateText}>
              {filterRole === 'All' && searchQuery === ''
                ? 'No team members added yet'
                : 'No team members match your filters'}
            </Text>
          </View>
        ) : (
          getSortedTeamMembers().map((member, idx) => (
            <View key={member.id || `member_${idx}`} style={styles.teamMemberCard}>
              <Image source={{ uri: (member.avatar && member.avatar.startsWith('http')) ? member.avatar : undefined }} style={styles.teamMemberAvatar} />
              <View style={styles.teamMemberInfo}>
                <Text style={styles.teamMemberName}>{member.name}</Text>
                <Text style={styles.teamMemberRole}>{member.role}</Text>
              </View>
              <View style={styles.teamMemberActions}>
                <TouchableOpacity
                  style={styles.teamMemberAction}
                  onPress={() => openEditMemberModal(member)}
                  disabled={isSaving}
                >
                  <Ionicons name="create-outline" size={20} color="#64748B" />
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.teamMemberAction}
                  onPress={() => openDeleteMemberConfirmation(member)}
                  disabled={isSaving}
                >
                  <Ionicons name="trash-outline" size={20} color="#64748B" />
                </TouchableOpacity>
              </View>
            </View>
          ))
        )}
      </ScrollView>

      {/* Add Team Member Modal */}
      <Modal animationType="slide" transparent visible={showAddMemberModal} onRequestClose={() => setShowAddMemberModal(false)}>
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Add Team Member</Text>
              <TouchableOpacity style={styles.modalCloseButton} onPress={() => setShowAddMemberModal(false)}>
                <Ionicons name="close" size={24} color="#64748B" />
              </TouchableOpacity>
            </View>
            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>Name *</Text>
              <TextInput
                style={styles.input}
                placeholder="Enter team member name"
                placeholderTextColor="#94A3B8"
                value={teamMember.name}
                onChangeText={(text) => setTeamMember({ ...teamMember, name: text })}
              />
            </View>
            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>Role *</Text>
              <TextInput
                style={styles.input}
                placeholder="Enter team member role"
                placeholderTextColor="#94A3B8"
                value={teamMember.role}
                onChangeText={(text) => setTeamMember({ ...teamMember, role: text })}
              />
            </View>
            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>Avatar</Text>
              <View style={styles.avatarPreviewContainer}>
                <Image source={{ uri: (teamMember.avatar && teamMember.avatar.startsWith('http')) ? teamMember.avatar : undefined }} style={styles.avatarPreview} />
              </View>
              <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.avatarOptions}>
                {avatarOptions.map((avatar, index) => (
                  <TouchableOpacity
                    key={index}
                    style={[styles.avatarOption, teamMember.avatar === avatar && styles.avatarOptionSelected]}
                    onPress={() => setTeamMember({ ...teamMember, avatar })}
                  >
                    <Image source={{ uri: (avatar && avatar.startsWith('http')) ? avatar : undefined }} style={styles.avatarOptionImage} />
                  </TouchableOpacity>
                ))}
              </ScrollView>
            </View>
            <TouchableOpacity style={styles.modalActionButton} onPress={handleAddTeamMember} disabled={isSaving}>
              <Text style={styles.modalActionButtonText}>Add Team Member</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      {/* Edit Team Member Modal */}
      <Modal animationType="slide" transparent visible={showEditMemberModal} onRequestClose={() => setShowEditMemberModal(false)}>
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Edit Team Member</Text>
              <TouchableOpacity style={styles.modalCloseButton} onPress={() => setShowEditMemberModal(false)}>
                <Ionicons name="close" size={24} color="#64748B" />
              </TouchableOpacity>
            </View>
            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>Name *</Text>
              <TextInput
                style={styles.input}
                placeholder="Enter team member name"
                placeholderTextColor="#94A3B8"
                value={teamMember.name}
                onChangeText={(text) => setTeamMember({ ...teamMember, name: text })}
              />
            </View>
            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>Role *</Text>
              <TextInput
                style={styles.input}
                placeholder="Enter team member role"
                placeholderTextColor="#94A3B8"
                value={teamMember.role}
                onChangeText={(text) => setTeamMember({ ...teamMember, role: text })}
              />
            </View>
            <View style={styles.inputGroup}>
              <Text style={styles.inputLabel}>Avatar</Text>
              <View style={styles.avatarPreviewContainer}>
                <Image source={{ uri: (teamMember.avatar && teamMember.avatar.startsWith('http')) ? teamMember.avatar : undefined }} style={styles.avatarPreview} />
              </View>
              <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.avatarOptions}>
                {avatarOptions.map((avatar, index) => (
                  <TouchableOpacity
                    key={index}
                    style={[styles.avatarOption, teamMember.avatar === avatar && styles.avatarOptionSelected]}
                    onPress={() => setTeamMember({ ...teamMember, avatar })}
                  >
                    <Image source={{ uri: (avatar && avatar.startsWith('http')) ? avatar : undefined }} style={styles.avatarOptionImage} />
                  </TouchableOpacity>
                ))}
              </ScrollView>
            </View>
            <TouchableOpacity style={styles.modalActionButton} onPress={handleEditTeamMember} disabled={isSaving}>
              <Text style={styles.modalActionButtonText}>Update Team Member</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      {/* Confirm Delete Modal */}
      <Modal animationType="fade" transparent visible={showConfirmDeleteModal} onRequestClose={() => setShowConfirmDeleteModal(false)}>
        <View style={styles.modalOverlay}>
          <View style={styles.confirmModalContent}>
            <MaterialCommunityIcons name="alert-circle-outline" size={40} color="#EF4444" />
            <Text style={styles.confirmModalTitle}>Remove Team Member?</Text>
            <Text style={styles.confirmModalText}>
              Are you sure you want to remove {selectedMember?.name} from the team? This action cannot be undone.
            </Text>
            <View style={styles.confirmModalButtons}>
              <TouchableOpacity
                style={styles.cancelModalButton}
                onPress={() => setShowConfirmDeleteModal(false)}
                disabled={isSaving}
              >
                <Text style={styles.cancelModalButtonText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.deleteModalButton} onPress={handleDeleteTeamMember} disabled={isSaving}>
                <Text style={styles.deleteModalButtonText}>Remove</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: { flex: 1, backgroundColor: '#4A90E2' },
  header: {
    backgroundColor: '#4A90E2',
    paddingVertical: 15,
    paddingHorizontal: 20,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backButton: { padding: 5 },
  headerCenter: { flexDirection: 'row', alignItems: 'center' },
  headerLogo: { width: 30, height: 30, borderRadius: 15, resizeMode: 'cover' },
  headerTitle: { color: '#fff', fontSize: 20, fontWeight: 'bold', marginLeft: 10 },
  loadingContainer: { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: '#f1f5f9' },
  loadingText: { marginTop: 10, fontSize: 16, color: '#64748B' },
  projectHeader: {
    backgroundColor: '#fff',
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  projectTitle: { fontSize: 16, fontWeight: '600', color: '#0f172a' },
  teamSummary: {
    backgroundColor: '#fff',
    paddingVertical: 12,
    paddingHorizontal: 20,
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  teamStat: { marginRight: 24 },
  teamStatValue: { fontSize: 20, fontWeight: '700', color: '#0e4e96' },
  teamStatLabel: { fontSize: 12, color: '#64748B' },
  searchFilterContainer: {
    backgroundColor: '#fff',
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f1f5f9',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    marginBottom: 12,
  },
  searchInput: { flex: 1, fontSize: 14, color: '#0f172a', marginLeft: 8, paddingVertical: 0 },
  filterContainer: { flexDirection: 'row', paddingVertical: 4 },
  filterOption: {
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 16,
    marginRight: 8,
    backgroundColor: '#f1f5f9',
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  filterOptionActive: { backgroundColor: '#0e4e96', borderColor: '#0e4e96' },
  filterOptionText: { fontSize: 12, color: '#64748B' },
  filterOptionTextActive: { color: '#fff', fontWeight: '600' },
  addButtonContainer: {
    backgroundColor: '#fff',
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  addButton: {
    backgroundColor: '#0e4e96',
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 16,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  addButtonText: { color: '#fff', fontSize: 14, fontWeight: '600', marginLeft: 8 },
  savingOverlay: {
    position: 'absolute',
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(255,255,255,0.8)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  savingText: { marginTop: 10, fontSize: 16, color: '#0e4e96', fontWeight: '600' },
  scrollContainer: { flex: 1, backgroundColor: '#f1f5f9' },
  contentContainer: { padding: 16, paddingBottom: 32 },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
    backgroundColor: '#fff',
    borderRadius: 12,
    marginTop: 16,
  },
  emptyStateText: { fontSize: 14, color: '#64748B', marginTop: 8 },
  teamMemberCard: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    flexDirection: 'row',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 10,
    elevation: 2,
  },
  teamMemberAvatar: { width: 50, height: 50, borderRadius: 25 },
  teamMemberInfo: { flex: 1, marginLeft: 12 },
  teamMemberName: { fontSize: 16, fontWeight: '600', color: '#0f172a', marginBottom: 2 },
  teamMemberRole: { fontSize: 14, color: '#64748B' },
  teamMemberActions: { flexDirection: 'row' },
  teamMemberAction: { padding: 8, marginLeft: 4 },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: '#fff',
    borderRadius: 12,
    width: '90%',
    padding: 20,
    maxHeight: '80%',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  modalTitle: { fontSize: 18, fontWeight: '600', color: '#0f172a' },
  modalCloseButton: { padding: 4 },
  inputGroup: { marginBottom: 16 },
  inputLabel: { fontSize: 14, fontWeight: '500', color: '#334155', marginBottom: 8 },
  input: {
    backgroundColor: '#f1f5f9',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 16,
    color: '#0f172a',
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  avatarPreviewContainer: { alignItems: 'center', marginVertical: 12 },
  avatarPreview: { width: 80, height: 80, borderRadius: 40 },
  avatarOptions: { flexDirection: 'row', paddingVertical: 8 },
  avatarOption: { marginHorizontal: 4, borderWidth: 2, borderColor: 'transparent', borderRadius: 25 },
  avatarOptionSelected: { borderColor: '#0e4e96' },
  avatarOptionImage: { width: 40, height: 40, borderRadius: 20 },
  modalActionButton: {
    backgroundColor: '#0e4e96',
    borderRadius: 8,
    paddingVertical: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalActionButtonText: { fontSize: 16, fontWeight: '600', color: '#fff' },
  confirmModalContent: {
    backgroundColor: '#fff',
    borderRadius: 12,
    width: '90%',
    padding: 20,
    alignItems: 'center',
  },
  confirmModalTitle: { fontSize: 18, fontWeight: '600', color: '#0f172a', marginTop: 12, marginBottom: 8 },
  confirmModalText: { fontSize: 14, color: '#64748B', textAlign: 'center', marginBottom: 20 },
  confirmModalButtons: { flexDirection: 'row', width: '100%' },
  cancelModalButton: {
    flex: 1,
    paddingVertical: 12,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 8,
    borderWidth: 1,
    borderColor: '#e2e8f0',
    borderRadius: 8,
  },
  cancelModalButtonText: { fontSize: 14, fontWeight: '600', color: '#64748B' },
  deleteModalButton: {
    flex: 1,
    paddingVertical: 12,
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: 8,
    backgroundColor: '#EF4444',
    borderRadius: 8,
  },
  deleteModalButtonText: { fontSize: 14, fontWeight: '600', color: '#fff' },
});